import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import {
  createAdminApi,
  CharacterDetail,
  CharacterAnimConfig,
  GestureDTO,
  GestureSynonym,
} from '../services/adminApi'
import styles from './CharactersPage.module.css'

interface Props {
  characterId: string
}


// ── Component ─────────────────────────────────────────────────────────────────

export default function CharacterDetailPage({ characterId }: Props) {
  const { apiKey } = useAdmin()

  const [character, setCharacter] = useState<CharacterDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // ── Animation config state ─────────────────────────────────────────────────

  const [animConfig, setAnimConfig] = useState<CharacterAnimConfig | null>(null)
  const [animLoading, setAnimLoading] = useState(true)
  const [animError, setAnimError] = useState('')

  // Gestures
  const [newGesture, setNewGesture] = useState('')
  const [gesturesSaving, setGesturesSaving] = useState(false)

  // Vocal cues
  const [newVocalCue, setNewVocalCue] = useState('')
  const [vocalCuesSaving, setVocalCuesSaving] = useState(false)

  // Synonyms
  const [showSynonyms, setShowSynonyms] = useState(true)
  const [synonymsDraft, setSynonymsDraft] = useState<GestureSynonym[]>([])
  const [synonymsSaving, setSynonymsSaving] = useState(false)
  const [newSynonymStems, setNewSynonymStems] = useState('')
  const [newSynonymGesture, setNewSynonymGesture] = useState('')

  // A2F config
  const [showA2F, setShowA2F] = useState(false)
  const [a2fConfigText, setA2fConfigText] = useState('')
  const [a2fSaving, setA2fSaving] = useState(false)
  const [a2fError, setA2fError] = useState('')

  // ── Load data ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    api.getCharacter(characterId)
      .then(setCharacter)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey, characterId])

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setAnimLoading(true)
    setAnimError('')
    api.getCharacterAnimConfig(characterId)
      .then(cfg => {
        setAnimConfig(cfg)
        setSynonymsDraft(cfg.gestureSynonyms)
        setA2fConfigText(JSON.stringify(cfg.a2fConfig, null, 2))
      })
      .catch(e => setAnimError(e instanceof Error ? e.message : String(e)))
      .finally(() => setAnimLoading(false))
  }, [apiKey, characterId])

  // ── Personality edit ───────────────────────────────────────────────────────

  function startEdit() {
    setDraft(character?.personality ?? '')
    setSaveError('')
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setSaveError('')
  }

  async function savePersonality() {
    if (!character) return
    const api = createAdminApi(() => apiKey)
    setSaving(true)
    setSaveError('')
    try {
      const updated = await api.updateCharacterPersonality(character.id, draft)
      setCharacter(updated)
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  // ── Gestures ───────────────────────────────────────────────────────────────

  async function addGesture() {
    const name = newGesture.trim()
    if (!name || !animConfig) return
    const updated = [...animConfig.gestures.map((g: GestureDTO) => g.name), name]
    setGesturesSaving(true)
    try {
      const result = await createAdminApi(() => apiKey).updateCharacterGestures(characterId, updated)
      setAnimConfig(result)
      setNewGesture('')
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setGesturesSaving(false)
    }
  }

  async function removeGesture(name: string) {
    if (!animConfig) return
    const updated = animConfig.gestures.filter((g: GestureDTO) => g.name !== name).map((g: GestureDTO) => g.name)
    setGesturesSaving(true)
    try {
      const result = await createAdminApi(() => apiKey).updateCharacterGestures(characterId, updated)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setGesturesSaving(false)
    }
  }

  // ── Vocal cues ─────────────────────────────────────────────────────────────

  async function addVocalCue() {
    const name = newVocalCue.trim()
    if (!name || !animConfig) return
    const updated = [...animConfig.vocalCues, name]
    setVocalCuesSaving(true)
    try {
      const result = await createAdminApi(() => apiKey).updateCharacterVocalCues(characterId, updated)
      setAnimConfig(result)
      setNewVocalCue('')
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setVocalCuesSaving(false)
    }
  }

  async function removeVocalCue(name: string) {
    if (!animConfig) return
    const updated = animConfig.vocalCues.filter(v => v !== name)
    setVocalCuesSaving(true)
    try {
      const result = await createAdminApi(() => apiKey).updateCharacterVocalCues(characterId, updated)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setVocalCuesSaving(false)
    }
  }

  // ── Synonyms ───────────────────────────────────────────────────────────────

  function addSynonymRow() {
    const stems = newSynonymStems.trim()
    const gesture = newSynonymGesture.trim()
    if (!stems || !gesture) return
    setSynonymsDraft(prev => [...prev, { stemWords: stems, gestureName: gesture }])
    setNewSynonymStems('')
    setNewSynonymGesture('')
  }

  function removeSynonymRow(idx: number) {
    setSynonymsDraft(prev => prev.filter((_, i) => i !== idx))
  }

  async function saveSynonyms() {
    setSynonymsSaving(true)
    try {
      const result = await createAdminApi(() => apiKey).updateCharacterGestureSynonyms(characterId, synonymsDraft)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setSynonymsSaving(false)
    }
  }

  // ── A2F config ─────────────────────────────────────────────────────────────

  async function saveA2FConfig() {
    setA2fSaving(true)
    setA2fError('')
    try {
      const config = JSON.parse(a2fConfigText)
      await createAdminApi(() => apiKey).updateCharacterA2FConfig(characterId, config)
    } catch (e) {
      setA2fError(e instanceof Error ? e.message : String(e))
    } finally {
      setA2fSaving(false)
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString()
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.detailPage}>
      <header className={styles.detailHeader}>
        <a href="#characters" className={styles.back}>← Characters</a>
        {character && (
          <>
            <h1>{character.name}</h1>
            {character.version > 1 && (
              <span className={styles.versionBadge}>v{character.version}</span>
            )}
          </>
        )}
      </header>

      <div className={styles.detailContent}>
        {loading && <p className={styles.empty}>Loading…</p>}
        {error && <p className={styles.error}>{error}</p>}

        {character && (
          <>
            {/* ── Identity ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Identity</p>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>ID</span>
                <span className={styles.fieldMono}>{character.id}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Name</span>
                <span className={styles.fieldValue}>{character.name}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Version</span>
                <span className={styles.fieldValue}>{character.version}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Created</span>
                <span className={styles.fieldValue}>{formatDate(character.createdAt)}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Updated</span>
                <span className={styles.fieldValue}>{formatDate(character.updatedAt)}</span>
              </div>
            </div>

            {/* ── Personality ── */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Personality</p>
                {!editing && (
                  <button className={styles.editBtn} onClick={startEdit}>Edit</button>
                )}
              </div>

              {editing ? (
                <>
                  <textarea
                    className={styles.personalityTextarea}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    rows={16}
                    disabled={saving}
                  />
                  {saveError && <p className={styles.saveError}>{saveError}</p>}
                  <div className={styles.editActions}>
                    <button className={styles.cancelBtn} onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={savePersonality} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              ) : (
                character.personality
                  ? <pre className={styles.personalityBlock}>{character.personality}</pre>
                  : <span className={styles.noStories}>No personality defined.</span>
              )}
            </div>

            {/* ── Gestures ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Gestures</p>
              {animLoading && <span className={styles.defaultNote}>Loading…</span>}
              {animError && <p className={styles.saveError}>{animError}</p>}
              {animConfig && (
                <>
                  {animConfig.gestures.length === 0 ? (
                    <span className={styles.defaultNote}>(no gestures — using global defaults)</span>
                  ) : (
                    <div className={styles.chipList}>
                      {animConfig.gestures.map((g: GestureDTO) => (
                        <span key={g.id} className={styles.chip}>
                          {g.name}
                          <button
                            className={styles.chipRemove}
                            onClick={() => removeGesture(g.name)}
                            disabled={gesturesSaving}
                            title="Remove"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={styles.addRow}>
                    <input
                      className={styles.addInput}
                      placeholder="New gesture name…"
                      value={newGesture}
                      onChange={e => setNewGesture(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addGesture()}
                      disabled={gesturesSaving}
                    />
                    <button
                      className={styles.addBtn}
                      onClick={addGesture}
                      disabled={gesturesSaving || !newGesture.trim()}
                    >Add</button>
                  </div>

                  {/* Synonyms sub-section */}
                  <button className={styles.synonymsToggle} onClick={() => setShowSynonyms(s => !s)}>
                    {showSynonyms ? 'Hide synonyms' : `Show synonyms (${synonymsDraft.length})`}
                  </button>
                  {showSynonyms && (
                    <>
                      <table className={styles.synonymTable}>
                        <thead>
                          <tr><th>Stems (comma-separated)</th><th>Maps to gesture</th><th /></tr>
                        </thead>
                        <tbody>
                          {synonymsDraft.map((s, i) => (
                            <tr key={i}>
                              <td>
                                <input
                                  className={styles.addInput}
                                  value={s.stemWords}
                                  onChange={e => {
                                    const next = [...synonymsDraft]
                                    next[i] = { ...next[i], stemWords: e.target.value }
                                    setSynonymsDraft(next)
                                  }}
                                  disabled={synonymsSaving}
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.addInput}
                                  value={s.gestureName}
                                  onChange={e => {
                                    const next = [...synonymsDraft]
                                    next[i] = { ...next[i], gestureName: e.target.value }
                                    setSynonymsDraft(next)
                                  }}
                                  disabled={synonymsSaving}
                                />
                              </td>
                              <td>
                                <button
                                  className={styles.chipRemove}
                                  onClick={() => removeSynonymRow(i)}
                                  disabled={synonymsSaving}
                                  title="Remove"
                                >×</button>
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td>
                              <input
                                className={styles.addInput}
                                placeholder="e.g. grin, smirk"
                                value={newSynonymStems}
                                onChange={e => setNewSynonymStems(e.target.value)}
                                disabled={synonymsSaving}
                              />
                            </td>
                            <td>
                              <input
                                className={styles.addInput}
                                placeholder="e.g. smile"
                                value={newSynonymGesture}
                                onChange={e => setNewSynonymGesture(e.target.value)}
                                disabled={synonymsSaving}
                              />
                            </td>
                            <td>
                              <button
                                className={styles.addBtn}
                                onClick={addSynonymRow}
                                disabled={synonymsSaving || !newSynonymStems.trim() || !newSynonymGesture.trim()}
                              >Add</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div className={styles.editActions}>
                        <button
                          className={styles.saveBtn}
                          onClick={saveSynonyms}
                          disabled={synonymsSaving}
                        >{synonymsSaving ? 'Saving…' : 'Save Synonyms'}</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* ── Vocal Cues ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Vocal Cues</p>
              {animConfig && (
                <>
                  {animConfig.vocalCues.length === 0 ? (
                    <span className={styles.defaultNote}>(no vocal cues — using global defaults)</span>
                  ) : (
                    <div className={styles.chipList}>
                      {animConfig.vocalCues.map(name => (
                        <span key={name} className={styles.chip}>
                          {name}
                          <button
                            className={styles.chipRemove}
                            onClick={() => removeVocalCue(name)}
                            disabled={vocalCuesSaving}
                            title="Remove"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={styles.addRow}>
                    <input
                      className={styles.addInput}
                      placeholder="New vocal cue name…"
                      value={newVocalCue}
                      onChange={e => setNewVocalCue(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addVocalCue()}
                      disabled={vocalCuesSaving}
                    />
                    <button
                      className={styles.addBtn}
                      onClick={addVocalCue}
                      disabled={vocalCuesSaving || !newVocalCue.trim()}
                    >Add</button>
                  </div>
                </>
              )}
            </div>

            {/* ── A2F Configuration ── */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>A2F Configuration</p>
                <button className={styles.a2fToggle} onClick={() => setShowA2F(s => !s)}>
                  {showA2F ? 'collapse' : 'expand'}
                </button>
              </div>
              {showA2F && (
                <>
                  <textarea
                    className={styles.personalityTextarea}
                    aria-label="A2F configuration JSON"
                    value={a2fConfigText}
                    onChange={e => setA2fConfigText(e.target.value)}
                    rows={16}
                    disabled={a2fSaving}
                  />
                  {a2fError && <p className={styles.saveError}>{a2fError}</p>}
                  <div className={styles.a2fActions}>
                    <button className={styles.saveBtn} onClick={saveA2FConfig} disabled={a2fSaving}>
                      {a2fSaving ? 'Saving…' : 'Save A2F Config'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ── Linked Stories ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Linked Stories ({character.stories.length})</p>
              {character.stories.length === 0 ? (
                <span className={styles.noStories}>No stories linked to this character.</span>
              ) : (
                <div className={styles.storyGrid}>
                  {character.stories.map(s => (
                    <div key={s.id} className={styles.storyCard}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardTitle}>{s.title || s.sourceFilename || s.id}</span>
                      </div>
                      <span className={styles.characterId}>{s.id}</span>
                      <div className={styles.storyCardFooter}>
                        {s.sourceFilename && (
                          <>
                            <a
                              className={styles.startChatBtn}
                              href={`?story=${encodeURIComponent(s.sourceFilename)}#chat`}
                            >
                              Start Chat
                            </a>
                            <a
                              className={styles.beginStoryBtn}
                              href={`?story=${encodeURIComponent(s.sourceFilename)}&features=storyteller#chat`}
                            >
                              Begin Story
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
